import React, { useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertCircle, Film, Image as ImageIcon, FileText, Globe, Search, Pencil, Trash2, Tag, Save, X, Link as LinkIcon, AlertTriangle, Copy, Upload } from "lucide-react";

/**
 * MediaTab — per-project Media Library.
 * Phase 2 upgrades:
 *   - search + library_kind type filter (image/video/panorama/pdf)
 *   - rename + tags
 *   - usage probe ("used in: scene X, unit Y")
 *   - safe delete (refuses 409 if used; offers Force delete in the UI)
 *   - copy URL helper
 *
 * The legacy filter chips (All / Processing / Optimized / Failed) move into
 * a secondary "status" row.
 */
const TYPE_FILTERS = [
  { key: "all", label: "All", Icon: null },
  { key: "image", label: "Images", Icon: ImageIcon },
  { key: "video", label: "Videos", Icon: Film },
  { key: "panorama", label: "Panoramas", Icon: Globe },
  { key: "pdf", label: "PDFs", Icon: FileText },
];
const STATUS_FILTERS = [
  { key: "all", label: "All status" },
  { key: "processing", label: "Processing" },
  { key: "ready", label: "Optimized" },
  { key: "failed", label: "Failed" },
  { key: "skipped", label: "Skipped" },
];

export default function MediaTab({ pid }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState({});
  const [editing, setEditing] = useState(null);   // media item being renamed
  const [usageDialog, setUsageDialog] = useState(null); // {media, usage}
  const [planUploads, setPlanUploads] = useState([]);
  const [uploadingPlans, setUploadingPlans] = useState(false);
  const planInputRef = useRef(null);
  const uploadInFlight = useRef(false);
  const latestLoad = useRef(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      if (status !== "all") params.set("status", status === "processing" ? "processing" : status);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/projects/${pid}/media?${params.toString()}`);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  latestLoad.current = load;

  const uploadFloorPlans = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || uploadInFlight.current) return;
    uploadInFlight.current = true;
    setUploadingPlans(true);
    setPlanUploads(files.map((file) => ({ name: file.name, status: "queued", progress: 0 })));
    const updateFile = (index, patch) => setPlanUploads((rows) => rows.map((row, i) => i === index ? { ...row, ...patch } : row));
    try {
      // One request at a time avoids competing large uploads. Never retry a POST
      // automatically: a lost response may still have stored the original file.
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        if (!file.type.startsWith("image/")) {
          updateFile(index, { status: "failed", error: "Choose an image file." });
          continue;
        }
        updateFile(index, { status: "uploading" });
        const fd = new FormData();
        fd.append("file", file);
        fd.append("project_id", pid);
        fd.append("kind", "upload");
        fd.append("preserve_original", "true");
        try {
          await api.post("/admin/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: ({ loaded, total }) => {
              if (total > 0) updateFile(index, { progress: Math.min(100, Math.round(loaded / total * 100)) });
            },
          });
          updateFile(index, { status: "uploaded", progress: 100 });
        } catch (err) {
          const uncertain = !err?.response || err.response.status >= 500;
          updateFile(index, {
            status: "failed",
            error: `${apiErrorMessage(err)}${uncertain ? " Check the library before uploading this file again; the server may have received it." : ""}`,
          });
        }
      }
    } finally {
      await latestLoad.current?.();
      uploadInFlight.current = false;
      setUploadingPlans(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid, type, status, q]);

  useEffect(() => {
    const inFlight = items.some((m) => m.processing_status === "pending" || m.processing_status === "processing");
    if (!inFlight) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const reprocess = async (mediaId) => {
    setBusyIds((b) => ({ ...b, [mediaId]: true }));
    try {
      await api.post(`/admin/media/${mediaId}/reprocess`);
      toast.success("Re-processing started");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusyIds((b) => { const n = { ...b }; delete n[mediaId]; return n; });
    }
  };

  const onDelete = async (media, force = false) => {
    try {
      await api.delete(`/admin/media/${media.id}${force ? "?force=true" : ""}`);
      toast.success("Deleted");
      setUsageDialog(null);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det) {
        setUsageDialog({ media, usage: det.used_in || [] });
        return;
      }
      toast.error(apiErrorMessage(e));
    }
  };

  const onShowUsage = async (media) => {
    try {
      const r = await api.get(`/admin/media/${media.id}/usage`);
      setUsageDialog({ media, usage: r.data?.used_in || [], readonly: true });
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const saveMeta = async (media, patch) => {
    try {
      await api.put(`/admin/media/${media.id}/meta`, patch);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const failedCount = counts.failed || 0;

  if (loading) return <div className="text-sm text-zinc-500" data-testid="media-tab-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="project-media-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search filename or tags…"
            className="w-72 pl-8 pr-3 py-1.5 rounded-md border border-zinc-300 text-sm outline-none focus:border-zinc-900"
            data-testid="media-tab-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="media-tab-type-filters">
          {TYPE_FILTERS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setType(key)}
              data-testid={`media-tab-type-${key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${type === key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
            >
              {Icon ? <Icon className="h-3 w-3" /> : null}
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => planInputRef.current?.click()}
            disabled={uploadingPlans}
            data-testid="media-upload-floor-plans"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60 text-xs"
          >
            {uploadingPlans ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload floor plans
          </button>
          <input ref={planInputRef} type="file" accept="image/*" multiple disabled={uploadingPlans} className="hidden" onChange={uploadFloorPlans} data-testid="media-floor-plans-input" />
          {failedCount > 0 && (
            <button
              onClick={async () => {
                const failed = items.filter((m) => m.processing_status === "failed");
                await Promise.all(failed.map((m) => api.post(`/admin/media/${m.id}/reprocess`)));
                toast.success(`${failed.length} re-processing started`);
                load();
              }}
              data-testid="media-retry-all-failed"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry {failedCount} failed
            </button>
          )}
          <button
            onClick={load}
            data-testid="media-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 hover:bg-zinc-50 text-xs"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {planUploads.length > 0 && (
        <div className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid="media-floor-plans-progress" aria-live="polite">
          <div className="text-sm font-medium">
            Floor plans: {planUploads.filter((file) => file.status === "uploaded").length}/{planUploads.length} uploaded
          </div>
          <p className="text-xs text-zinc-500">Original image quality and filenames are preserved. Select uploaded plans in the unit editor to link them to apartments.</p>
          <ul className="max-h-64 overflow-auto space-y-2">
            {planUploads.map((file, index) => (
              <li key={index} className="text-xs" data-testid={`media-floor-plan-upload-${index}`} data-status={file.status}>
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="break-all text-zinc-800">{file.name}</span>
                  <span className={file.status === "failed" ? "text-rose-700" : "text-zinc-500"}>
                    {file.status === "queued" ? "Queued" : file.status === "uploaded" ? "Uploaded" : file.status === "failed" ? "Failed" : file.progress === 100 ? "Saving…" : `Uploading… ${file.progress}%`}
                  </span>
                </div>
                {file.status === "uploading" && <progress value={file.progress} max="100" className="w-full" aria-label={`Uploading ${file.name}`} />}
                {file.error && <p className="mt-1 text-rose-700">{file.error}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-1.5" data-testid="media-tab-status-filters">
        {STATUS_FILTERS.map((f) => {
          const c = f.key === "all"
            ? items.length
            : f.key === "processing"
              ? (counts.pending || 0) + (counts.processing || 0)
              : counts[f.key] || 0;
          const active = status === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              data-testid={`media-filter-${f.key}`}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full transition ${active ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"}`}
            >
              {f.label} <span className={active ? "text-white/85" : "text-zinc-400"}>{c}</span>
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-lg p-8 text-center" data-testid="media-tab-empty">
          {q || type !== "all" || status !== "all"
            ? "No media match these filters."
            : "No uploads yet. Upload a video or image to a Scene/Unit to populate the library."}
        </div>
      ) : (
        <div className="grid gap-3" data-testid="media-tab-list">
          {items.map((m) => (
            <MediaRow
              key={m.id} media={m}
              onReprocess={reprocess}
              onRename={() => setEditing(m)}
              onDelete={() => onDelete(m)}
              onShowUsage={() => onShowUsage(m)}
              busy={!!busyIds[m.id]}
            />
          ))}
        </div>
      )}

      {editing && (
        <RenameModal media={editing} onCancel={() => setEditing(null)} onSave={saveMeta} />
      )}
      {usageDialog && (
        <UsageDialog
          media={usageDialog.media} usage={usageDialog.usage}
          readonly={usageDialog.readonly}
          onClose={() => setUsageDialog(null)}
          onForceDelete={() => onDelete(usageDialog.media, true)}
        />
      )}
    </div>
  );
}

function libraryKindOf(m) {
  if (m.library_kind) return m.library_kind;
  const ct = (m.content_type || "").toLowerCase();
  if (ct.startsWith("video/")) return "video";
  if (ct === "application/pdf") return "pdf";
  if (ct.startsWith("image/")) {
    if ((m.tags || []).includes("panorama")) return "panorama";
    if (m.video_width && m.video_height && Math.abs(m.video_width / m.video_height - 2) < 0.1) return "panorama";
    return "image";
  }
  return "other";
}

function MediaRow({ media, onReprocess, onRename, onDelete, onShowUsage, busy }) {
  const s = media.processing_status;
  const lk = libraryKindOf(media);
  const ThumbIcon = lk === "video" ? Film : lk === "pdf" ? FileText : lk === "panorama" ? Globe : ImageIcon;

  const copyUrl = () => {
    if (!media.url) return;
    const abs = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + media.url;
    navigator.clipboard.writeText(abs).then(() => toast.success("URL copied"), () => toast.error("Copy failed"));
  };

  return (
    <div className="flex items-start gap-4 border border-zinc-200 rounded-lg p-3 hover:border-zinc-300 transition" data-testid={`media-row-${media.id}`} data-status={s}>
      <div className="w-24 h-16 rounded-md overflow-hidden bg-zinc-100 flex items-center justify-center shrink-0 relative">
        {media.poster_url ? (
          <img src={media.poster_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (lk === "image" || lk === "panorama") && media.url ? (
          <img src={media.thumbnail_url || media.url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <ThumbIcon className="h-6 w-6 text-zinc-400" />
        )}
        {lk === "panorama" && (
          <span className="absolute top-0.5 right-0.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-black/55 text-white">360°</span>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-zinc-900 font-medium truncate" title={media.title || media.original_filename}>
            {media.title || media.original_filename || media.id}
          </div>
          <StatusPill status={s} preserveOriginal={media.preserve_original} />
          {(media.tags || []).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 inline-flex items-center gap-1">
              <Tag className="h-2.5 w-2.5" />{t}
            </span>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 flex gap-3 flex-wrap">
          {media.content_type && <span>{media.content_type}</span>}
          {media.original_size && <span>{formatKB(media.original_size)}</span>}
          {media.duration_seconds && <span>{media.duration_seconds.toFixed(1)}s</span>}
          {media.video_width && <span>{media.video_width}×{media.video_height}</span>}
          {s === "ready" && media.optimization_warning && (
            <span className="text-zinc-500" data-testid={`media-optimal-${media.id}`}>· already optimal</span>
          )}
          {s === "ready" && !media.optimization_warning && media.processed_size && (
            <span className="text-emerald-700" data-testid={`media-saved-${media.id}`}>→ {formatKB(media.processed_size)} ({formatPct(media.original_size, media.processed_size)})</span>
          )}
          {media.created_at && <span>· {new Date(media.created_at).toLocaleDateString()}</span>}
        </div>
        {s === "failed" && media.processing_error && (
          <div className="text-[11px] text-rose-700 mt-1 truncate" title={media.processing_error}>{media.processing_error}</div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1.5">
        <button onClick={copyUrl} title="Copy URL" className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`media-copy-${media.id}`}>
          <Copy className="h-3.5 w-3.5 text-zinc-500" />
        </button>
        <button onClick={onShowUsage} title="Where used" className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`media-usage-${media.id}`}>
          <LinkIcon className="h-3.5 w-3.5 text-zinc-500" />
        </button>
        <button onClick={onRename} title="Rename / tag" className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`media-rename-${media.id}`}>
          <Pencil className="h-3.5 w-3.5 text-zinc-500" />
        </button>
        {(s === "ready" || s === "failed") && (
          <button onClick={() => onReprocess(media.id)} disabled={busy} title={s === "failed" ? "Retry" : "Re-optimize"}
            className={`p-1.5 rounded ${s === "failed" ? "hover:bg-rose-50 text-rose-700" : "hover:bg-zinc-100 text-zinc-600"} disabled:opacity-60`}
            data-testid={`media-row-reprocess-${media.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        )}
        <button onClick={onDelete} title="Delete" className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`media-delete-${media.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function RenameModal({ media, onCancel, onSave }) {
  const [title, setTitle] = useState(media.title || media.original_filename || "");
  const [tags, setTags] = useState((media.tags || []).join(", "));
  return (
    <div className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onCancel} data-testid="media-rename-modal">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium">Rename / tag</h3>
          <button onClick={onCancel}><X className="h-4 w-4 text-zinc-400" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-xs text-zinc-600 mb-1 block">Title</span>
          <input className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="media-rename-title" />
        </label>
        <label className="block mb-3">
          <span className="text-xs text-zinc-600 mb-1 block">Tags (comma-separated)</span>
          <input className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="lobby, exterior, hero" data-testid="media-rename-tags" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-1.5 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={() => onSave(media, { title, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) })} className="px-4 py-1.5 rounded-md text-sm bg-zinc-900 text-white inline-flex items-center gap-1.5" data-testid="media-rename-save">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function UsageDialog({ media, usage, readonly, onClose, onForceDelete }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onClose} data-testid="media-usage-dialog">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium inline-flex items-center gap-2">
            {!readonly && usage.length > 0 && <AlertTriangle className="h-4 w-4 text-amber-600" />}
            {readonly ? "Used in" : usage.length > 0 ? "Media is in use" : "Confirm delete"}
          </h3>
          <button onClick={onClose}><X className="h-4 w-4 text-zinc-400" /></button>
        </div>
        <div className="text-sm text-zinc-700 mb-3 truncate" title={media.title || media.original_filename}>
          {media.title || media.original_filename}
        </div>
        {usage.length === 0 ? (
          <div className="text-sm text-zinc-500">Not used anywhere.</div>
        ) : (
          <ul className="space-y-1.5 max-h-60 overflow-auto" data-testid="media-usage-list">
            {usage.map((u, i) => (
              <li key={i} className="text-xs flex items-center gap-2 px-2 py-1 rounded bg-zinc-50">
                <span className="uppercase tracking-wider text-[9px] text-zinc-500 w-16">{u.kind}</span>
                <span className="text-zinc-900 truncate">{u.label || u.id}</span>
                <span className="text-zinc-400 text-[10px] ml-auto">{u.field}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-1.5 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">Close</button>
          {!readonly && usage.length > 0 && (
            <button onClick={onForceDelete} className="px-4 py-1.5 rounded-md text-sm bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5" data-testid="media-force-delete">
              <Trash2 className="h-4 w-4" /> Delete anyway
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, preserveOriginal }) {
  const map = {
    pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <Loader2 className="h-3 w-3 animate-spin" />, text: "Pending" },
    processing: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <Loader2 className="h-3 w-3 animate-spin" />, text: "Processing…" },
    ready: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle2 className="h-3 w-3" />, text: "Optimized" },
    failed: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle className="h-3 w-3" />, text: "Failed" },
    skipped: { cls: "bg-zinc-50 text-zinc-600 border-zinc-200", icon: null, text: "Skipped" },
  };
  const m = preserveOriginal && status === "skipped"
    ? { ...map.skipped, text: "Original quality" }
    : map[status] || map.skipped;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${m.cls}`} data-testid={`media-pill-${status}`}>
      {m.icon}{m.text}
    </span>
  );
}

function formatKB(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatPct(orig, processed) {
  if (!orig || !processed) return "";
  const ratio = processed / orig;
  if (ratio < 1) return `−${Math.round((1 - ratio) * 100)}%`;
  return `+${Math.round((ratio - 1) * 100)}%`;
}
